// File: webelieve-frontend/src/features/admin/components/settings/workflow-builder/WorkflowList.tsx
// EF-7E: Workflow list — shows all workflows with actions per card

import React, { useState, useEffect } from 'react';
import { workflowApi, type IntakeWorkflow, type WorkflowTriggerType } from './workflowApi';

interface WorkflowListProps {
  onEdit: (workflowId: string) => void;
  onCreate: () => void;
}

const TRIGGER_LABELS: Record<WorkflowTriggerType, string> = {
  stage_change: 'Stage Change',
  form_submission: 'Form Submission',
  time_elapsed: 'Time Elapsed',
  document_uploaded: 'Document Uploaded',
};

const TRIGGER_COLORS: Record<WorkflowTriggerType, string> = {
  stage_change: 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent',
  form_submission: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  time_elapsed: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  document_uploaded: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
};

const WorkflowList: React.FC<WorkflowListProps> = ({ onEdit, onCreate }) => {
  const [workflows, setWorkflows] = useState<IntakeWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await workflowApi.listWorkflows();
      setWorkflows(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWorkflows(); }, []);

  const handleToggle = async (workflowId: string) => {
    try {
      await workflowApi.toggleActive(workflowId);
      await loadWorkflows();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDuplicate = async (workflowId: string) => {
    try {
      await workflowApi.duplicateWorkflow(workflowId);
      await loadWorkflows();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (workflowId: string, name: string) => {
    if (!window.confirm(`Delete workflow "${name}"? This action cannot be undone.`)) return;
    try {
      await workflowApi.deleteWorkflow(workflowId);
      await loadWorkflows();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading workflows...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">{error}</p>
        <button onClick={loadWorkflows} className="mt-2 text-sm text-red-600 dark:text-red-400 underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Intake Workflows</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Automate lead processing with configurable trigger-action workflows
          </p>
        </div>
        <button
          onClick={onCreate}
          className="px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-md hover:bg-lex-accent-hover transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Workflow
        </button>
      </div>

      {/* Empty state */}
      {workflows.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.38.138.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No workflows yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Create your first workflow to automate lead processing
          </p>
          <button
            onClick={onCreate}
            className="px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-md hover:bg-lex-accent-hover"
          >
            Create Workflow
          </button>
        </div>
      )}

      {/* Workflow cards */}
      <div className="space-y-3">
        {workflows.map(wf => (
          <div
            key={wf.workflow_id}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-sm transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {wf.name}
                  </h4>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${TRIGGER_COLORS[wf.trigger_type]}`}>
                    {TRIGGER_LABELS[wf.trigger_type]}
                  </span>
                  {wf.is_active ? (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                      Inactive
                    </span>
                  )}
                </div>
                {wf.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-1">{wf.description}</p>
                )}
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span>{wf.actions.length} action{wf.actions.length !== 1 ? 's' : ''}</span>
                  <span>{wf.total_executions} execution{wf.total_executions !== 1 ? 's' : ''}</span>
                  {wf.last_executed_at && (
                    <span>Last run: {new Date(wf.last_executed_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 ml-4">
                <button
                  onClick={() => onEdit(wf.workflow_id)}
                  className="p-1.5 text-gray-400 hover:text-lex-accent dark:hover:text-lex-accent hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Edit"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleToggle(wf.workflow_id)}
                  className={`p-1.5 rounded transition-colors ${
                    wf.is_active
                      ? 'text-green-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                      : 'text-gray-400 hover:text-green-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title={wf.is_active ? 'Deactivate' : 'Activate'}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={
                      wf.is_active
                        ? 'M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z'
                        : 'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z'
                    } />
                  </svg>
                </button>
                <button
                  onClick={() => handleDuplicate(wf.workflow_id)}
                  className="p-1.5 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Duplicate"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(wf.workflow_id, wf.name)}
                  className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkflowList;
